<template>
    <DashedCard
        :title="$t('karafun.titleKarafun')"
        title-has-bg="bgTitle ppv2-py-[1px] ppv2-text-black"
        min-height="85px"
        :margin-bottom="45"
        :margin-top="38"
        :custom-style="{ padding: normalizeSize(27, true) }"
        custom-class="ppv2-opacity-80 hover:ppv2-opacity-100"
        custom-class-title="!ppv2-px-[10px] !ppv2-top-[-8px] ppv2-rounded-[21px] "
    >
        <template #content>
            <div class="ppv2-flex ppv2-flex-col ppv2-w-full" :style="{ gap: normalizeSize(16, true) }">
                <!-- Sync Button -->
                <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <BaseButton
                        type="button"
                        variant="primary"
                        :disabled="syncing"
                        @click="syncDevices"
                        customClass="!ppv2-bg-[#7d2dbd] !ppv2-border-[#7d2dbd]"
                        :customHeight="36"
                        :customFontSize="13"
                    >
                        <span
                            v-if="syncing"
                            class="ppv2-inline-block ppv2-h-4 ppv2-w-4 ppv2-animate-spin ppv2-rounded-full ppv2-border-2 ppv2-border-solid ppv2-border-current ppv2-border-r-transparent ppv2-align-[-0.125em] ppv2-motion-reduce:animate-[spin_1.5s_linear_infinite] ppv2-mr-2"
                            role="status"
                        ></span>
                        {{ $t('karafun.syncKarafun') }}
                    </BaseButton>
                    <span v-if="syncMessage" :class="syncSuccess ? 'ppv2-text-green-500' : 'ppv2-text-red-500'">
                        {{ syncMessage }}
                    </span>
                </div>

                <!-- Sync Button -->

                <div class="ppv2-py-3 ppv2-max-w-[60%] ppv2-min-w-52">
                    <BaseSelect
                        v-model="internalSelectedDevices"
                        option-label="name"
                        option-value="id"
                        :options="availableDevices"
                        :label="$t('karafun.selectKarafun')"
                        multiple
                        :close-on-select="false"
                        class="karafun-device-select"
                    >
                        <template #option="{ option }">
                            <div v-if="option" class="ppv2-flex ppv2-items-center">
                                <div
                                    class="ppv2-w-4 ppv2-h-4 ppv2-border ppv2-rounded ppv2-mr-2 ppv2-flex ppv2-items-center ppv2-justify-center"
                                    :class="
                                        isDeviceSelected(option.id)
                                            ? 'ppv2-bg-primary ppv2-border-primary'
                                            : 'ppv2-border-gray-400'
                                    "
                                >
                                    <span v-if="isDeviceSelected(option.id)" class="ppv2-text-white ppv2-text-xs">✓</span>
                                </div>
                                <!-- Use explicit property access -->
                                <span>{{ option.name }}</span>
                            </div>
                        </template>
                    </BaseSelect>
                </div>
            </div>
        </template>
    </DashedCard>
</template>

<script>
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'

export default {
    name: 'KarafunDevice',
    components: { BaseSelect, DashedCard, BaseButton },

    props: {
        // List of all devices (optional, can be fetched internally)
        devices: {
            type: [Array, Object], // Accept Object to avoid prop validation error if parent passes wrong type
            default: () => [],
        },
        // Selected devices passed from parent
        selectedDevices: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            // Local copy to allow internal editing
            internalSelectedDevices: Array.isArray(this.selectedDevices) ? [...this.selectedDevices] : [],
            internalDevices: [], // API fetched devices
            loadingDevices: false,
            syncing: false,
            syncMessage: '',
            syncSuccess: false,
        }
    },
    computed: {
        // Use prop devices if valid array and not empty, otherwise use internal fetched devices
        availableDevices() {
            // Case 1: Standard Array
            if (Array.isArray(this.devices) && this.devices.length > 0) {
                return this.devices
            }

            // Case 2: Object (e.g. {0: {id:1}, 1: {id:2}, locale: 'fr'})
            // We need to convert it to array and filter out non-device properties like 'locale'
            if (this.devices && typeof this.devices === 'object' && Object.keys(this.devices).length > 0) {
                const extracted = Object.values(this.devices).filter(
                    (item) =>
                        item &&
                        typeof item === 'object' &&
                        Object.prototype.hasOwnProperty.call(item, 'id') &&
                        Object.prototype.hasOwnProperty.call(item, 'name')
                )
                if (extracted.length > 0) return extracted
            }

            // Case 3: Fallback to internal fetched devices
            return Array.isArray(this.internalDevices) ? this.internalDevices : []
        },
    },
    methods: {
        normalizeSize,
        async fetchDevices() {
            this.loadingDevices = true
            try {
                const response = await axios.get('/api/v1/karafun/list')
                let data = response.data.devices || response.data

                // Normalize if it's the object format (e.g. {0: {..}, 1: {..}, locale: 'fr'})
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    data = Object.values(data).filter(
                        (item) =>
                            item &&
                            typeof item === 'object' &&
                            Object.prototype.hasOwnProperty.call(item, 'id') &&
                            Object.prototype.hasOwnProperty.call(item, 'name')
                    )
                }

                this.internalDevices = Array.isArray(data) ? data : []
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.internalDevices = []
            } finally {
                this.loadingDevices = false
            }
        },
        async syncDevices() {
            this.syncing = true
            this.syncMessage = ''
            this.syncSuccess = false

            try {
                await axios.post(
                    '/api/v1/karafun/sync',
                    {},
                    {
                        headers: {
                            'X-API-SECRET': '1234', // Harcoded in blade example as '1234', usually from config/env
                        },
                    }
                )

                this.syncSuccess = true
                this.syncMessage = this.$t('karafun.syncSuccess')

                // Refresh list after sync
                await this.fetchDevices()
                this.$emit('sync')
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Karafun sync error:', error)
                this.syncSuccess = false
                this.syncMessage =
                    this.$t('karafun.syncError') + (error.response?.data?.error ? ': ' + error.response.data.error : '')
            } finally {
                this.syncing = false
                // Clear message after a few seconds
                if (this.syncSuccess) {
                    setTimeout(() => {
                        this.syncMessage = ''
                    }, 3000)
                }
            }
        },
        isDeviceSelected(id) {
            // Check for inclusion with type loose comparison if needed (though IDs should be consistent)
            // Handle both string/number mismatch
            return this.internalSelectedDevices.some((d) => String(d) === String(id))
        },
    },
    watch: {
        // When internal selection changes, emit update to parent
        internalSelectedDevices(newVal) {
            this.$emit('update:selectedDevices', newVal)
        },
        // Keep local value in sync when parent updates it
        selectedDevices(newVal) {
            this.internalSelectedDevices = newVal
        },
    },
    mounted() {
        // If no devices passed via props, fetch them
        if (!this.devices || (Array.isArray(this.devices) && this.devices.length === 0)) {
            this.fetchDevices()
        }
    },
}
</script>
<style>
.bgTitle {
    background-color: #fff !important;
}
.dark .bgTitle {
    background-color: #141243 !important;
}
/* Hide selected items in dropdown */
.karafun-device-select .vs__dropdown-option--selected {
    display: none !important;
}
</style>
